import React, { useState, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import { useAppSelector } from '../../../state/store';
import {
  getActiveMarker,
  getCurrentLocation,
  getSensorsById,
  getSensorsByLocId,
} from '../../../state/selectors';
import SensorIcon from '../../../styles/icons/SensorIcon';
import SensorsInSubLoc from './SensorsInSubLoc';
import { varNameDetails } from '../../../utils/varNames';
import SensorListItem from './SensorListItem';

interface SensorsInLocProps {
  taggedSensors: string[];
  handleTagging: (value: string) => void;
}

export enum SelectionType {
  All = 'All',
  SelectedVarName = 'SelectedVarName',
}

function SensorsInLoc({ taggedSensors, handleTagging }: SensorsInLocProps) {
  const theme = useTheme();
  const currentLocation = useAppSelector(getCurrentLocation);
  const sensorsById = useAppSelector(getSensorsById);
  const sensorsInLoc = useAppSelector(getSensorsByLocId).get(currentLocation);
  const activeMarker = useAppSelector(getActiveMarker);
  const [selectionType, setSelectionType] = useState(SelectionType.All);

  // filter sensors that are already selected for grouping
  const sensorsList = useMemo(() => {
    const filteredSensorsInLoc =
      sensorsInLoc?.filter((item) => !taggedSensors.includes(item)) ?? [];
    let sensors = filteredSensorsInLoc;
    if (selectionType === SelectionType.SelectedVarName) {
      sensors = filteredSensorsInLoc.filter((sensor) =>
        sensorsById.get(sensor)?.data?.some((datum) => datum.varName === activeMarker)
      );
    }

    return sensors;
  }, [activeMarker, taggedSensors, selectionType, sensorsById, sensorsInLoc]);

  // sort sensors by name
  const sortedSensorsList = useMemo(
    () =>
      [...sensorsList].sort((a, b) => {
        const nameA = sensorsById.get(a)?.name || a;
        const nameB = sensorsById.get(b)?.name || b;
        return nameA.localeCompare(nameB);
      }),
    [sensorsById, sensorsList]
  );

  const SelectionIcon = varNameDetails[activeMarker].icon ?? SensorIcon;

  return (
    <Box sx={{ background: theme.palette.primary.main }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ alignSelf: 'center' }}>
          Sensors In Location
        </Typography>
        <ToggleButtonGroup
          color="primary"
          size="small"
          value={selectionType}
          exclusive
          onChange={(e, value) => {
            // MUI passes null when the active button is clicked again
            // (exclusive deselect) — ignore that and keep current selection.
            if (value !== null) setSelectionType(value);
          }}
          aria-label="selection-type"
        >
          <ToggleButton sx={{ textTransform: 'inherit' }} value={SelectionType.All}>
            All
          </ToggleButton>
          <ToggleButton sx={{ textTransform: 'none' }} value={SelectionType.SelectedVarName}>
            <SelectionIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <List>
        {sortedSensorsList.map((sensor) => (
          <SensorListItem key={sensor} sensorId={sensor} handleTagging={handleTagging} />
        ))}
      </List>

      {sensorsList.length === 0 && <Alert severity="error">No sensors available in location</Alert>}

      <SensorsInSubLoc
        taggedSensors={taggedSensors}
        handleTagging={handleTagging}
        showAllSensors={selectionType === SelectionType.All}
      />
    </Box>
  );
}

export default SensorsInLoc;
